/**
 * MP Bundle picker: fills a bundle product's slots from a curated pool via a
 * side drawer (search + category tabs + product rows), then hands the choice
 * to the cart.
 *
 * How it hands it over depends on the bundle. A bundle whose pool comes from a
 * collection posts the picked variants to /cart/add.js as their own lines,
 * tagged with a shared `_bundle_group`, and the Cart Transform function merges
 * them into one bundle line. A bundle still configured with
 * variant-reference pools lets the theme's product form submit instead, with
 * the selection serialized into a hidden `_bundle_components` property and one
 * readable `Produkt N` property per slot, and the function expands that line.
 *
 * The slot count comes from the `custom.bundle_size` metafield, so this file
 * never assumes how many there are. It is written against a list of groups
 * rather than one because the markup used to split perfumes from diffusers;
 * the picker now renders a single merged group, and the shape still holds.
 *
 * A group's selection is an ordered list of *units*, not one item per slot:
 * adding the same fragrance twice appends two units and therefore fills two
 * consecutive slots. That is why each row carries a quantity stepper rather
 * than a one-shot add button, and why the drawer stays open until the
 * customer closes it.
 */
(function () {
  'use strict';

  var TAB_KEYS = ['all', 'her', 'him', 'unisex', 'new'];

  function matchesTab(item, key) {
    if (key === 'all') return true;
    if (key === 'new') return !!item.new;
    return item.cat === key;
  }

  /**
   * Polish needs three plural forms; "Dodaj jeszcze 1 produkt / 2 produkty /
   * 5 produktów" all appear in the drawer footer as slots get filled.
   */
  function plural(n, one, few, many) {
    if (n === 1) return one;
    var mod10 = n % 10;
    var mod100 = n % 100;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
    return many;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function itemLabel(item) {
    return item.opt ? item.title + ' — ' + item.opt : item.title;
  }

  /* ------------------------------------------------------------------ *
   * Search
   * ------------------------------------------------------------------ */

  /**
   * Lowercases and strips accents, so "lancome" finds "Lancôme".
   *
   * Customers type without diacritics far more often than with them, and this
   * catalogue is full of names that carry them — Lancôme, Hermès, Armani Sì.
   * NFD splits an accented letter into letter + combining mark, which the
   * range below removes. Polish ł has no decomposition, so it is mapped by
   * hand; every other Polish letter (ą ć ę ń ó ś ź ż) does decompose.
   */
  function fold(text) {
    var lowered = String(text == null ? '' : text).toLowerCase();
    if (lowered.normalize) {
      lowered = lowered.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }
    return lowered.replace(/ł/g, 'l');
  }

  /**
   * Precomputes what search reads: the name the customer sees, and everything
   * else worth matching (vendor, tags) kept apart from it.
   *
   * They stay separate so a hit in the name can outrank a hit in the tags. The
   * tag "Męskie perfumy" sits on hundreds of products, so without that,
   * searching "perfumy" returns the whole pool in catalogue order.
   */
  function prepareForSearch(item) {
    item.searchName = fold(itemLabel(item));
    item.searchRest = fold(item.s || '');
    return item;
  }

  function queryTokens(query) {
    return fold(query).split(/\s+/).filter(Boolean);
  }

  /**
   * Every token has to appear somewhere, in any order — so "million paco"
   * finds "Paco Rabanne One Million" even though the words are the other way
   * round and not adjacent. Returns null when the item does not match at all,
   * otherwise a score: tokens found in the name are worth more than tokens
   * found only in vendor or tags, and a name starting with the query wins.
   */
  function scoreItem(item, tokens) {
    var score = 0;

    for (var i = 0; i < tokens.length; i++) {
      var token = tokens[i];
      if (item.searchName.indexOf(token) !== -1) {
        score += 10;
      } else if (item.searchRest.indexOf(token) !== -1) {
        score += 1;
      } else {
        return null;
      }
    }

    if (item.searchName.indexOf(tokens[0]) === 0) score += 5;
    return score;
  }

  /* ------------------------------------------------------------------ *
   * Selection model
   * ------------------------------------------------------------------ */

  function isFull(group) {
    return group.selection.length >= group.slots.length;
  }

  function isComplete(state) {
    return state.groups.every(isFull);
  }

  function quantityOf(group, id) {
    var count = 0;
    for (var i = 0; i < group.selection.length; i++) {
      if (group.selection[i].id === id) count++;
    }
    return count;
  }

  function addUnit(group, item) {
    if (isFull(group)) return false;
    group.selection.push(item);
    return true;
  }

  function removeUnit(group, id) {
    for (var i = group.selection.length - 1; i >= 0; i--) {
      if (group.selection[i].id === id) {
        group.selection.splice(i, 1);
        return true;
      }
    }
    return false;
  }

  /* ------------------------------------------------------------------ *
   * Slot rendering
   * ------------------------------------------------------------------ */

  function renderSlot(slot, item) {
    slot.el.classList.toggle('is-filled', !!item);
    slot.clearBtn.hidden = !item;

    if (!item) {
      slot.labelEl.textContent = slot.placeholder;
      slot.thumbEl.hidden = true;
      slot.thumbEl.innerHTML = '';
      slot.iconEl.hidden = false;
      slot.openBtn.setAttribute('aria-label', slot.placeholder);
      return;
    }

    slot.labelEl.textContent = itemLabel(item);
    slot.iconEl.hidden = !!item.img;
    slot.thumbEl.hidden = !item.img;
    slot.thumbEl.innerHTML = '';
    if (item.img) {
      var img = document.createElement('img');
      img.src = item.img;
      img.alt = '';
      img.loading = 'lazy';
      slot.thumbEl.appendChild(img);
    }
    slot.openBtn.setAttribute('aria-label', slot.placeholder + ': ' + item.title);
  }

  function renderGroup(group) {
    group.slots.forEach(function (slot, index) {
      renderSlot(slot, group.selection[index] || null);
    });
    if (group.counter) {
      group.counter.textContent = group.selection.length + '/' + group.slots.length;
    }
  }

  /* ------------------------------------------------------------------ *
   * Form sync
   * ------------------------------------------------------------------ */

  function syncForm(state) {
    var complete = isComplete(state);
    var items = [];
    var index = {};
    var selectedProducts = [];

    state.groups.forEach(function (group) {
      renderGroup(group);

      group.selection.forEach(function (item) {
        selectedProducts.push(item);
        if (index[item.id]) {
          index[item.id].quantity++;
          return;
        }
        index[item.id] = { id: item.id, quantity: 1 };
        items.push(index[item.id]);
      });
    });

    if (state.componentsInput) {
      // Compact `variantId:quantity` pairs — the format the bundle Cart
      // Transform function parses (see the bundle-cart-transform extension in
      // magiczne-perfumy-extensions). Deliberately not JSON: the function runs
      // as WebAssembly and its runtime only decodes values the platform itself
      // parses, so JSON here would mean linking a JSON parser into the module
      // to read forty bytes.
      state.componentsInput.value = complete
        ? items
            .map(function (item) {
              return item.id + ':' + item.quantity;
            })
            .join(',')
        : '';
    }
    state.productInputs.forEach(function (input, productIndex) {
      var item = selectedProducts[productIndex];
      input.value = complete && item ? itemLabel(item) : '';
    });
    if (state.submitBtn) state.submitBtn.disabled = state.variantUnavailable || !complete;
    if (state.hint) {
      // The intro above the picker carries the standing copy, so this element
      // is silent unless something goes wrong: whatever message it borrowed
      // must not survive the next change of selection.
      state.hint.textContent = '';
      state.hint.hidden = true;
    }
  }

  /* ------------------------------------------------------------------ *
   * Pool loading
   *
   * A collection-backed pool is fetched rather than printed into the page.
   * `paginate` caps `collection.products` at 250, so a server-rendered island
   * would silently lose everything past the 250th product — and this pool is
   * 354. The feed comes from templates/collection.mp-bundle-pool.liquid, which
   * lists every variant of every product in the collection; picking the right
   * one is done here, against the same option values the Cart Transform
   * function checks, so the two cannot disagree about what belongs.
   * ------------------------------------------------------------------ */

  function categoryOf(type, tags) {
    var haystack = (type + ' ' + tags).toLowerCase();
    if (haystack.indexOf('unisex') !== -1) return ['unisex', 'Unisex'];
    if (haystack.indexOf('damsk') !== -1) return ['her', 'Dla Niej'];
    if (haystack.indexOf('mesk') !== -1 || haystack.indexOf('męsk') !== -1) return ['him', 'Dla Niego'];
    return ['', ''];
  }

  function formatMoney(minorUnits) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'PLN';
    try {
      return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: currency }).format(minorUnits / 100);
    } catch (err) {
      return (minorUnits / 100).toFixed(2);
    }
  }

  /** The first available variant whose title contains every wanted value. */
  function pickVariant(entry, wanted) {
    for (var i = 0; i < entry.vs.length; i++) {
      var variant = entry.vs[i];
      if (!variant[3]) continue;
      var title = variant[1];
      var matched = wanted.every(function (value) {
        return title.indexOf(value) !== -1;
      });
      if (matched) return variant;
    }
    return null;
  }

  function itemFrom(entry, variant) {
    var category = categoryOf(entry.y || '', entry.g || '');
    var tags = (entry.g || '').toLowerCase();
    var isDefault = entry.vs.length === 1 && variant[1] === 'Default Title';

    return {
      id: variant[0],
      title: entry.t,
      opt: isDefault ? '' : variant[1],
      img: entry.i || '',
      price: formatMoney(variant[2]),
      cat: category[0],
      catLabel: category[1],
      new: tags.indexOf('nowoś') !== -1 || tags.indexOf('nowos') !== -1,
      best: tags.indexOf('bestseller') !== -1,
      s: (entry.t + ' ' + (entry.v || '') + ' ' + variant[1] + ' ' + tags).toLowerCase(),
    };
  }

  function fetchPool(state) {
    if (state.poolPromise) return state.poolPromise;

    var items = [];

    function loadPage(number) {
      var url = '/collections/' + state.poolCollection + '?view=mp-bundle-pool&page=' + number;
      return fetch(url, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('pool page ' + number);
          return response.json();
        })
        .then(function (data) {
          (data.items || []).forEach(function (entry) {
            var variant = pickVariant(entry, state.poolOptions);
            if (variant) items.push(prepareForSearch(itemFrom(entry, variant)));
          });
          if (data.page < data.pages) return loadPage(number + 1);
          return items;
        });
    }

    state.poolPromise = loadPage(1)
      .then(function (loaded) {
        state.pools[state.groups[0].key] = loaded;
        state.poolLoaded = true;
        return loaded;
      })
      .catch(function (error) {
        // Let a later open try again rather than leaving the drawer
        // permanently empty on one flaky request.
        state.poolPromise = null;
        state.poolFailed = true;
        throw error;
      });

    return state.poolPromise;
  }

  /* ------------------------------------------------------------------ *
   * Add to cart (merge bundles)
   *
   * A merge bundle never puts its own product in the cart. The picked
   * variants go in as ordinary cart lines sharing a random `_bundle_group`
   * and the parent's `_bundle_parent`, and the Cart Transform function merges
   * them back into one bundle line. That is what lets the pool come from a
   * collection of any size — see snippets/mp-bundle-picker-group.liquid.
   *
   * Bundles still configured with variant-reference pools take the original
   * path instead: the theme's own product form submits, one line goes in
   * carrying `_bundle_components`, and the function expands it.
   * ------------------------------------------------------------------ */

  function cartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function setBusy(state, busy) {
    if (!state.submitBtn) return;
    state.submitBtn.classList.toggle('loading', busy);
    state.submitBtn.setAttribute('aria-disabled', busy ? 'true' : 'false');
    var spinner = state.submitBtn.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !busy);
  }

  function showMessage(state, message) {
    if (!state.hint) return;
    state.hint.textContent = message || state.hintDefault;
    state.hint.hidden = !message;
  }

  function clearSelection(state) {
    state.groups.forEach(function (group) {
      group.selection.length = 0;
    });
    syncForm(state);
  }

  /**
   * One entry per distinct variant with a real quantity, not one per slot.
   * Four slots holding the same fragrance are one cart line of four, which is
   * also how the function counts units.
   */
  function selectedItems(state) {
    var counts = {};
    var order = [];

    state.groups.forEach(function (group) {
      group.selection.forEach(function (item) {
        if (!counts[item.id]) {
          counts[item.id] = 0;
          order.push(item.id);
        }
        counts[item.id]++;
      });
    });

    return order.map(function (id) {
      return { id: id, quantity: counts[id] };
    });
  }

  function addBundleToCart(state) {
    if (state.adding) return;
    state.adding = true;
    setBusy(state, true);
    showMessage(state, '');

    var properties = {
      // Random so several bundles can sit in one cart without their lines
      // bleeding into each other.
      _bundle_group: 'b' + Math.random().toString(36).slice(2, 10),
      _bundle_parent: state.parentVariantId,
    };

    var body = {
      items: selectedItems(state).map(function (item) {
        return { id: item.id, quantity: item.quantity, properties: properties };
      }),
    };

    var cart = cartElement();
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map(function (section) {
        return section.id;
      });
      body.sections_url = window.location.pathname;
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }

    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

    fetch(addUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (parsed) {
        if (parsed.status) {
          showMessage(state, parsed.description || parsed.message || 'Nie udało się dodać zestawu.');
          return;
        }

        clearSelection(state);

        if (cart && typeof cart.renderContents === 'function') {
          // renderContents expects the one line that was added; a multi-item
          // add has no single line, so point it at the first.
          var first = (parsed.items || [])[0];
          if (first) {
            parsed.key = first.key;
            parsed.id = first.variant_id;
          }
          cart.renderContents(parsed);
          return;
        }

        window.location = (window.routes && window.routes.cart_url) || '/cart';
      })
      .catch(function () {
        showMessage(state, 'Nie udało się dodać zestawu do koszyka. Spróbuj ponownie.');
      })
      .finally(function () {
        state.adding = false;
        setBusy(state, false);
      });
  }

  /* ------------------------------------------------------------------ *
   * Drawer
   * ------------------------------------------------------------------ */

  function visibleItems(state, ignoreTab) {
    var pool = state.pools[state.activeGroup.key] || [];
    var tokens = queryTokens(state.query);

    if (!tokens.length) {
      return ignoreTab
        ? pool.slice()
        : pool.filter(function (item) {
            return matchesTab(item, state.activeTab);
          });
    }

    var scored = [];
    pool.forEach(function (item, position) {
      if (!ignoreTab && !matchesTab(item, state.activeTab)) return;
      var score = scoreItem(item, tokens);
      if (score === null) return;
      scored.push({ item: item, score: score, position: position });
    });

    // Best match first, catalogue order among equals.
    scored.sort(function (a, b) {
      return b.score - a.score || a.position - b.position;
    });

    return scored.map(function (entry) {
      return entry.item;
    });
  }

  function renderTabs(state) {
    var pool = state.pools[state.activeGroup.key] || [];
    state.tabsEl.innerHTML = '';

    var available = TAB_KEYS.filter(function (key) {
      if (key === 'all') return true;
      return pool.some(function (item) {
        return matchesTab(item, key);
      });
    });

    // A lone "Wszystkie" tab is noise — hide the whole strip instead.
    if (available.length < 2) {
      state.tabsEl.hidden = true;
      return;
    }
    state.tabsEl.hidden = false;

    available.forEach(function (key) {
      var button = el('button', 'mp-bundle-drawer__tab', state.labels[key]);
      button.type = 'button';
      button.setAttribute('role', 'tab');
      button.dataset.tab = key;
      var active = key === state.activeTab;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
      if (key === 'new') button.classList.add('mp-bundle-drawer__tab--new');
      state.tabsEl.appendChild(button);
    });
  }

  function renderStepper(state, item, qty, full) {
    var stepper = el('div', 'mp-bundle-stepper');

    var minus = el('button', 'mp-bundle-stepper__button', '−');
    minus.type = 'button';
    minus.dataset.step = 'down';
    minus.dataset.id = String(item.id);
    minus.setAttribute('aria-label', 'Usuń jedną sztukę: ' + item.title);
    stepper.appendChild(minus);

    var count = el('span', 'mp-bundle-stepper__count', String(qty));
    count.setAttribute('aria-live', 'polite');
    stepper.appendChild(count);

    // At a full bundle there is nothing left to add, so the plus goes away
    // rather than sitting there disabled.
    if (!full) {
      var plus = el('button', 'mp-bundle-stepper__button', '+');
      plus.type = 'button';
      plus.dataset.step = 'up';
      plus.dataset.id = String(item.id);
      plus.setAttribute('aria-label', 'Dodaj jeszcze jedną sztukę: ' + item.title);
      stepper.appendChild(plus);
    }

    return stepper;
  }

  function renderRow(state, item) {
    var group = state.activeGroup;
    var qty = quantityOf(group, item.id);
    var full = isFull(group);

    var row = el('div', 'mp-bundle-row');
    row.classList.toggle('is-selected', qty > 0);
    row.classList.toggle('is-disabled', qty === 0 && full);

    var media = el('div', 'mp-bundle-row__media');
    if (item.img) {
      var img = document.createElement('img');
      img.src = item.img;
      img.alt = '';
      img.loading = 'lazy';
      media.appendChild(img);
    }
    row.appendChild(media);

    var body = el('div', 'mp-bundle-row__body');
    body.appendChild(el('p', 'mp-bundle-row__title', itemLabel(item)));

    var meta = el('div', 'mp-bundle-row__meta');
    if (item.catLabel) {
      meta.appendChild(el('span', 'mp-bundle-badge mp-bundle-badge--' + (item.cat || 'plain'), item.catLabel));
    }
    if (state.showPrices && item.price) {
      meta.appendChild(el('span', 'mp-bundle-row__price', item.price));
    }
    if (meta.childNodes.length) body.appendChild(meta);
    row.appendChild(body);

    var side = el('div', 'mp-bundle-row__side');
    if (item.best) {
      side.appendChild(el('span', 'mp-bundle-pill', state.labels.bestseller));
    }

    if (qty > 0) {
      side.appendChild(renderStepper(state, item, qty, full));
    } else {
      var add = el('button', 'mp-bundle-row__add');
      add.type = 'button';
      add.dataset.step = 'up';
      add.dataset.id = String(item.id);
      add.textContent = state.labels.add;
      add.appendChild(el('span', 'mp-bundle-row__plus', '+'));
      add.disabled = full;
      side.appendChild(add);
    }

    row.appendChild(side);
    return row;
  }

  function renderList(state) {
    var items = visibleItems(state);

    state.listEl.innerHTML = '';
    if (!items.length) {
      // "nothing matches your search", "nothing in this category" and "the
      // pool has not arrived yet" look identical to a customer unless they
      // are told apart.
      if (state.poolCollection && !state.poolLoaded) {
        var pending = state.poolFailed ? state.labels.poolError : state.labels.poolLoading;
        state.listEl.appendChild(el('p', 'mp-bundle-drawer__empty', pending));
        return;
      }

      var query = state.query.trim();
      var elsewhere = query && state.activeTab !== 'all' ? visibleItems(state, true).length : 0;

      state.listEl.appendChild(
        el(
          'p',
          'mp-bundle-drawer__empty',
          query ? 'Brak wyników dla „' + query + '”' + (elsewhere ? ' w tej kategorii.' : '.') : state.labels.empty
        )
      );

      if (elsewhere) {
        var showAll = el(
          'button',
          'mp-bundle-drawer__show-all',
          'Pokaż ' + elsewhere + ' ' + plural(elsewhere, 'wynik', 'wyniki', 'wyników') + ' we wszystkich kategoriach'
        );
        showAll.type = 'button';
        showAll.setAttribute('data-show-all', '');
        state.listEl.appendChild(showAll);
      }
      return;
    }

    var fragment = document.createDocumentFragment();
    items.forEach(function (item) {
      fragment.appendChild(renderRow(state, item));
    });
    state.listEl.appendChild(fragment);
  }

  function renderFooter(state) {
    var group = state.activeGroup;
    var remaining = group.slots.length - group.selection.length;

    state.footerEl.innerHTML = '';

    if (remaining > 0) {
      state.footerEl.classList.remove('is-complete');
      var noun = plural(remaining, 'produkt', 'produkty', 'produktów');
      var text = 'Dodaj jeszcze ' + remaining + ' ' + noun;
      if (state.footerSuffix) text += ', ' + state.footerSuffix;
      state.footerEl.appendChild(el('p', 'mp-bundle-drawer__progress', text));
      return;
    }

    state.footerEl.classList.add('is-complete');
    state.footerEl.appendChild(el('p', 'mp-bundle-drawer__progress is-complete', state.labels.complete));

    // Every group full means the PDP form is submittable; offer that here so
    // a finished bundle does not require hunting for the page behind the
    // drawer. Otherwise nudge the customer to the group still missing items.
    var pending = state.groups.filter(function (candidate) {
      return !isFull(candidate);
    });

    if (!pending.length && state.submitBtn && !state.variantUnavailable) {
      var submit = el('button', 'mp-bundle-drawer__submit', state.labels.submit);
      submit.type = 'button';
      submit.dataset.submit = 'true';
      state.footerEl.appendChild(submit);
      return;
    }

    var next = el('button', 'mp-bundle-drawer__submit mp-bundle-drawer__submit--secondary', state.labels.close);
    next.type = 'button';
    next.setAttribute('data-mp-bundle-close', '');
    state.footerEl.appendChild(next);
  }

  function renderDrawer(state) {
    renderTabs(state);
    renderList(state);
    renderFooter(state);
  }

  function openDrawer(state, group, returnFocusTo) {
    state.activeGroup = group;
    state.returnFocusTo = returnFocusTo;
    state.query = '';
    state.activeTab = 'all';
    state.searchInput.value = '';

    renderDrawer(state);
    state.listEl.scrollTop = 0;

    if (state.poolCollection && !state.poolLoaded) {
      fetchPool(state)
        .then(function () {
          if (state.drawer.classList.contains('is-open')) renderDrawer(state);
        })
        .catch(function () {
          if (state.drawer.classList.contains('is-open')) renderDrawer(state);
        });
    }

    state.drawer.classList.add('is-open');
    state.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mp-bundle-drawer-open');

    // Focusing the search box on touch pops the keyboard over the list, so
    // only steal focus when the customer is on a pointer device.
    if (window.matchMedia('(hover: hover)').matches) {
      state.searchInput.focus();
    } else {
      state.drawer.querySelector('[data-mp-bundle-close]').focus();
    }
  }

  function closeDrawer(state) {
    if (!state.drawer.classList.contains('is-open')) return;
    state.drawer.classList.remove('is-open');
    state.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mp-bundle-drawer-open');
    if (state.returnFocusTo) state.returnFocusTo.focus();
    state.returnFocusTo = null;
  }

  function findItem(state, id) {
    var pool = state.pools[state.activeGroup.key] || [];
    for (var i = 0; i < pool.length; i++) {
      if (pool[i].id === id) return pool[i];
    }
    return null;
  }

  /**
   * Re-render in place so the drawer keeps its scroll position — stepping a
   * row near the bottom of a long pool must not jump the list back to the top.
   */
  function refreshDrawer(state) {
    var scrollTop = state.listEl.scrollTop;
    renderList(state);
    renderFooter(state);
    state.listEl.scrollTop = scrollTop;
  }

  function step(state, id, direction) {
    var group = state.activeGroup;
    var changed;

    if (direction === 'up') {
      var item = findItem(state, id);
      changed = item ? addUnit(group, item) : false;
    } else {
      changed = removeUnit(group, id);
    }

    if (!changed) return;
    syncForm(state);
    refreshDrawer(state);
  }

  /* ------------------------------------------------------------------ *
   * Wiring
   * ------------------------------------------------------------------ */

  function readPools(container) {
    var pools = {};
    container.querySelectorAll('[data-mp-bundle-pool]').forEach(function (node) {
      var key = node.getAttribute('data-mp-bundle-pool');
      try {
        pools[key] = (JSON.parse(node.textContent) || []).map(prepareForSearch);
      } catch (err) {
        pools[key] = [];
      }
    });
    return pools;
  }

  function readGroups(container) {
    return Array.prototype.map.call(container.querySelectorAll('[data-mp-bundle-group]'), function (groupEl) {
      var group = {
        key: groupEl.getAttribute('data-mp-bundle-group'),
        el: groupEl,
        counter: groupEl.querySelector('[data-mp-bundle-counter]'),
        slots: [],
        selection: [],
      };
      groupEl.querySelectorAll('[data-mp-bundle-slot]').forEach(function (slotEl) {
        var labelEl = slotEl.querySelector('[data-mp-bundle-slot-label]');
        group.slots.push({
          el: slotEl,
          group: group,
          openBtn: slotEl.querySelector('[data-mp-bundle-slot-open]'),
          clearBtn: slotEl.querySelector('[data-mp-bundle-slot-clear]'),
          labelEl: labelEl,
          thumbEl: slotEl.querySelector('[data-mp-bundle-slot-thumb]'),
          iconEl: slotEl.querySelector('.mp-bundle-slot__icon'),
          placeholder: labelEl ? labelEl.textContent.trim() : '',
        });
      });
      return group;
    });
  }

  function findSlot(state, slotEl) {
    for (var i = 0; i < state.groups.length; i++) {
      var slots = state.groups[i].slots;
      for (var j = 0; j < slots.length; j++) {
        if (slots[j].el === slotEl) return { group: state.groups[i], slot: slots[j], index: j };
      }
    }
    return null;
  }

  function bindPicker(container) {
    if (!container || container.__mpBundleBound) return;
    container.__mpBundleBound = true;

    var drawer = container.querySelector('[data-mp-bundle-drawer]');
    if (!drawer) return;

    var formId = container.dataset.form;
    var form = formId ? document.getElementById(formId) : null;
    var submitBtn = form ? form.querySelector('[type="submit"]') : null;

    var state = {
      container: container,
      drawer: drawer,
      pools: readPools(container),
      groups: readGroups(container),
      submitBtn: submitBtn,
      variantUnavailable: submitBtn ? submitBtn.disabled : false,
      hint: container.querySelector('[data-mp-bundle-hint]'),
      hintDefault: '',
      componentsInput: container.querySelector('[data-mp-bundle-components]'),
      productInputs: Array.prototype.slice.call(container.querySelectorAll('[data-mp-bundle-product]')),
      tabsEl: drawer.querySelector('[data-mp-bundle-tabs]'),
      listEl: drawer.querySelector('[data-mp-bundle-list]'),
      footerEl: drawer.querySelector('[data-mp-bundle-footer]'),
      searchInput: drawer.querySelector('[data-mp-bundle-search]'),
      sectionId: container.dataset.section || '',
      merge: container.dataset.merge === 'true',
      poolCollection: container.dataset.poolCollection || '',
      poolOptions: (container.dataset.poolOptions || '')
        .split(',')
        .map(function (value) {
          return value.trim();
        })
        .filter(Boolean),
      poolPromise: null,
      poolLoaded: false,
      poolFailed: false,
      parentVariantId: container.dataset.parentVariant || '',
      adding: false,
      showPrices: drawer.dataset.showPrices === 'true',
      footerSuffix: drawer.dataset.footerSuffix || '',
      labels: {
        all: drawer.dataset.labelAll,
        her: drawer.dataset.labelHer,
        him: drawer.dataset.labelHim,
        unisex: drawer.dataset.labelUnisex,
        new: drawer.dataset.labelNew,
        add: drawer.dataset.labelAdd,
        bestseller: drawer.dataset.labelBestseller,
        empty: drawer.dataset.labelEmpty,
        complete: drawer.dataset.labelComplete,
        submit: drawer.dataset.labelSubmit,
        close: drawer.dataset.labelClose,
        poolLoading: 'Wczytuję produkty...',
        poolError: 'Nie udało się wczytać listy produktów. Zamknij i spróbuj ponownie.',
      },
      query: '',
      activeTab: 'all',
      activeGroup: null,
      returnFocusTo: null,
    };

    if (state.hint) state.hintDefault = state.hint.dataset.defaultMessage || '';

    // position: fixed is relative to the nearest transformed ancestor, and the
    // PDP wrapper animates — reparent to <body> so the panel always covers the
    // viewport.
    document.body.appendChild(drawer);

    container.addEventListener('click', function (event) {
      var openBtn = event.target.closest('[data-mp-bundle-slot-open]');
      if (openBtn) {
        var found = findSlot(state, openBtn.closest('[data-mp-bundle-slot]'));
        if (found) openDrawer(state, found.group, openBtn);
        return;
      }

      var clearBtn = event.target.closest('[data-mp-bundle-slot-clear]');
      if (clearBtn) {
        var cleared = findSlot(state, clearBtn.closest('[data-mp-bundle-slot]'));
        if (cleared && cleared.group.selection[cleared.index]) {
          cleared.group.selection.splice(cleared.index, 1);
          syncForm(state);
        }
      }
    });

    drawer.addEventListener('click', function (event) {
      if (event.target.closest('[data-mp-bundle-close]')) {
        closeDrawer(state);
        return;
      }

      if (event.target.closest('[data-submit]')) {
        closeDrawer(state);
        if (state.submitBtn) state.submitBtn.click();
        return;
      }

      if (event.target.closest('[data-show-all]')) {
        state.activeTab = 'all';
        renderTabs(state);
        renderList(state);
        state.listEl.scrollTop = 0;
        return;
      }

      var tab = event.target.closest('[data-tab]');
      if (tab) {
        state.activeTab = tab.dataset.tab;
        renderTabs(state);
        renderList(state);
        state.listEl.scrollTop = 0;
        return;
      }

      var stepBtn = event.target.closest('[data-step]');
      if (stepBtn && !stepBtn.disabled) {
        step(state, parseInt(stepBtn.dataset.id, 10), stepBtn.dataset.step);
      }
    });

    state.searchInput.addEventListener('input', function () {
      state.query = state.searchInput.value;
      renderList(state);
      state.listEl.scrollTop = 0;
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      if (!drawer.classList.contains('is-open')) return;
      closeDrawer(state);
    });

    // Changing the variant makes the theme re-render the product info and call
    // toggleAddButton with whatever the new markup says, which clears the lock
    // an incomplete bundle put on the button. variantChange is published right
    // after that, so re-applying our own state here wins.
    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.variantChange
    ) {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        if (!event || !event.data) return;
        // Quick-add modals and featured-product sections publish the same event
        // for a different product; acting on those would lock this picker's
        // button against a variant that has nothing to do with it.
        if (state.sectionId && event.data.sectionId !== state.sectionId) return;

        var variant = event.data.variant;
        state.variantUnavailable = !variant || variant.available === false;
        syncForm(state);
      });
    }

    // A disabled button is a hint, not a guarantee — anything that re-enables it
    // between our sync and the click would otherwise send a bundle line with no
    // components, or a line for a variant that is sold out. Capture on document
    // so this runs before the theme's own submit handler on the form.
    if (form) {
      document.addEventListener(
        'submit',
        function (event) {
          if (event.target !== form) return;

          if (!isComplete(state) || state.variantUnavailable) {
            event.preventDefault();
            event.stopImmediatePropagation();
            // The hint is about unfilled slots, so it stays hidden when the
            // block came from an unavailable variant instead.
            if (!isComplete(state)) showMessage(state, state.hintDefault);
            // Slots fill in order, so the first empty one sits at the end of
            // the current selection.
            var pending = null;
            state.groups.some(function (group) {
              if (isFull(group)) return false;
              pending = group.slots[group.selection.length];
              return !!pending;
            });
            if (pending) pending.openBtn.focus();
            return;
          }

          // A merge bundle's own product must never reach the cart: the picked
          // variants go in as their own lines instead. Legacy pool bundles fall
          // through to the theme's form, which adds the bundle line the expand
          // path needs.
          if (!state.merge) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          addBundleToCart(state);
        },
        true
      );
    }

    syncForm(state);

    if (state.poolCollection) {
      var warm = function () {
        fetchPool(state).catch(function () {});
      };
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(warm, { timeout: 3000 });
      } else {
        setTimeout(warm, 1200);
      }
    }
  }

  function bindAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-mp-bundle-picker]').forEach(bindPicker);
  }

  window.MpBundlePicker = {
    bind: bindPicker,
    bindAll: bindAll,
  };

  function init() {
    bindAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event || !event.target) return;
    bindAll(event.target);
  });
})();
